/**
 * FASE 20.5 — COGNITIVE QUERY RESOLUTION
 *
 * Maps natural language + runtime context + temporal intent to ranked tool candidates.
 * Returns at most 2 candidates — never issues queries itself.
 *
 * Score range: 0–1.0.
 * Threshold for execution: ≥ 0.30
 */

import { getAllDataTools, getDataToolById } from "./toolSchemas";
import { isRoleAllowed }                   from "./permissionFilters";

const MAX_CANDIDATES = 2;
const EXECUTE_THRESHOLD = 0.30;

/* ─── Signal → tool affinity map ─────────────────────────────── */

const SIGNAL_TOOL_MAP = {
  "no_recent_visit":        ["get_people_needing_followup", "get_upcoming_visits"],
  "no_baptism":             ["get_member_status"],
  "upcoming_event":         ["get_upcoming_events"],
  "unresolved_prayer":      ["get_recent_prayers"],
  "family_at_risk":         ["get_family_overview", "get_visit_timeline"],
  "inactive_family":        ["get_family_overview", "get_people_needing_followup"],
  "new_member":             ["get_member_status"],
  "pastoral_transition":    ["get_church_activity_summary"],
};

/* ─── Intent → tool affinity map ─────────────────────────────── */

const INTENT_TOOL_MAP = {
  events:          ["get_upcoming_events"],
  visit_plan:      ["get_today_visits", "get_next_visit", "get_upcoming_visits", "get_visit_timeline"],
  planning:        ["get_today_visits", "get_upcoming_events", "get_week_schedule"],
  analysis:        ["get_church_activity_summary", "get_member_status", "get_dashboard_snapshot"],
  family_followup: ["get_family_overview", "get_people_needing_followup"],
  prayer:          ["get_recent_prayers"],
  general:         ["get_dashboard_snapshot"],
};

/* ─── Module → tool affinity map ─────────────────────────────── */

const MODULE_TOOL_MAP = {
  "visitas":         ["get_today_visits", "get_upcoming_visits", "get_next_visit", "get_visit_timeline"],
  "familias":        ["get_family_overview", "get_people_needing_followup"],
  "personas":        ["get_member_status", "get_people_needing_followup"],
  "oraciones":       ["get_recent_prayers"],
  "eventos":         ["get_upcoming_events"],
  "iglesias":        ["get_church_activity_summary"],
  "inicio":          ["get_dashboard_snapshot"],
  "predicaciones":   ["get_dashboard_snapshot"],
  "kevia":           ["get_dashboard_snapshot"],
  "revisión":        ["get_church_activity_summary", "get_member_status"],
  "materiales":      ["get_church_activity_summary"],
  "junta directiva": ["get_member_status"],
};

/* ─── Score accumulator ────────────────────────────────────────── */

function buildScoreMap(signals = [], intent = "general", currentModule = "inicio", entityContext = null, temporalIntent = null) {
  const scores = {};

  function add(toolId, weight) {
    scores[toolId] = (scores[toolId] ?? 0) + weight;
  }

  // ── Temporal overrides (highest priority) ──────────────────────
  // These fire before intent/module scoring to ensure the right tool
  // wins when the user explicitly signals a time horizon.
  if (temporalIntent === "next") {
    if (intent === "visit_plan" || intent === "planning") {
      add("get_next_visit",      0.70);
      add("get_upcoming_visits", 0.45);
    }
    if (intent === "events" || intent === "planning") {
      add("get_upcoming_events", 0.45);
    }
  } else if (temporalIntent === "week") {
    add("get_week_schedule", 0.70);
  } else if (temporalIntent === "pending") {
    if (intent === "visit_plan" || intent === "planning") {
      add("get_upcoming_visits", 0.65);
    }
  } else if (temporalIntent === "today") {
    if (intent === "visit_plan" || intent === "planning") {
      add("get_today_visits", 0.70);
    }
  } else if (temporalIntent === "recent") {
    if (intent === "visit_plan") {
      add("get_visit_timeline", 0.60);
    }
    if (intent === "prayer" || intent === "general") {
      add("get_recent_prayers", 0.50);
    }
  } else if (temporalIntent === "active") {
    add("get_recent_prayers",          0.45);
    add("get_people_needing_followup", 0.40);
  }

  // ── Signal contributions ────────────────────────────────────────
  for (const signal of signals) {
    const toolIds  = SIGNAL_TOOL_MAP[signal?.id] ?? [];
    const sigScore = Math.min(1, (signal?.score ?? 0.5) * (signal?.confidence ?? 1.0));
    for (const id of toolIds) add(id, sigScore * 0.55);
  }

  // ── Intent contribution ─────────────────────────────────────────
  const intentTools = INTENT_TOOL_MAP[intent] ?? INTENT_TOOL_MAP.general;
  for (const id of intentTools) add(id, 0.30);

  // ── Module contribution ─────────────────────────────────────────
  const moduleTools = MODULE_TOOL_MAP[currentModule] ?? MODULE_TOOL_MAP.inicio;
  for (const id of moduleTools) add(id, 0.20);

  // ── Entity context boost ────────────────────────────────────────
  if (entityContext?.id) {
    add("get_family_overview", 0.15);
    add("get_visit_timeline",  0.15);
  }

  return scores;
}

/* ─── Public API ─────────────────────────────────────────────── */

/**
 * @typedef {Object} DataIntentCandidate
 * @property {string}  toolId
 * @property {number}  score
 * @property {string}  reason
 * @property {boolean} shouldExecute
 * @property {Object}  filters
 */

/**
 * Resolve data tool candidates from current runtime context + temporal intent.
 *
 * @param {Object} params
 * @param {Object[]} [params.signals]        — active signals from kevia-signals
 * @param {string}  [params.intent]          — detected intent from orchestrateKevia
 * @param {string}  [params.temporalIntent]  — from parseTemporalIntent()
 * @param {string}  [params.currentModule]   — from kevia-runtime
 * @param {Object}  [params.entityContext]   — from KeviaContext / entity tracker
 * @param {Object}  [params.appScope]        — role, district_id, church_id
 * @returns {DataIntentCandidate[]}
 */
export function resolveDataIntent({ signals = [], intent = "general", temporalIntent = null, currentModule = "inicio", entityContext = null, appScope = {} }) {
  const role     = appScope?.role ?? "*";
  const scoreMap = buildScoreMap(signals, intent, currentModule, entityContext, temporalIntent);

  const candidates = Object.entries(scoreMap)
    .map(([toolId, score]) => {
      const schema = getDataToolById(toolId);
      if (!schema) return null;
      if (!isRoleAllowed(schema, role)) return null;

      const filters = {};
      if (entityContext?.id && schema.allowedFilters.includes("entity_id")) {
        filters.entity_id = entityContext.id;
      }

      return {
        toolId,
        score:         Math.min(1, score),
        reason:        schema.description,
        shouldExecute: score >= EXECUTE_THRESHOLD,
        filters,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_CANDIDATES);

  return candidates;
}

/**
 * Get the top candidate that should auto-execute (score ≥ threshold).
 * Returns null if no confident candidate.
 */
export function getTopExecutableCandidate(candidates) {
  return candidates.find((c) => c.shouldExecute) ?? null;
}
